import { useEffect, useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft, MapPin, Warehouse, Truck, Bot, TrendingUp, AlertTriangle, Clock, Copy, Check,
  DollarSign, ShieldAlert, Users2, PackageCheck, ArrowRightLeft, Zap, CloudLightning, Route, Factory, Activity,
  RefreshCw, Info, X,
} from 'lucide-react'
import UITooltip from '../components/shared/Tooltip.jsx'
import client from '../api/client.js'

const DC_INFO = {
  HZL: 'Hazleton, PA — AEO DC #002959',
  MCI: 'Ottawa, KS — AEO DC #000659',
  CDC: 'Mississauga, ON — AEC DC #000978',
  LA_3PL: 'Los Angeles, CA — 3rd-party logistics',
}
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { useStore, useStoreActions, useActionExplain, useStoreTrafficPrediction } from '../hooks/useStore.js'
import { leadTimeHours, formatLeadTime, leadTimeTier } from '../utils/leadTime.js'
import { useNow } from '../hooks/useNow.js'
import WeatherBadge from '../components/shared/WeatherBadge.jsx'
import SeverityBar from '../components/shared/SeverityBar.jsx'
import SignalBadge from '../components/shared/SignalBadge.jsx'
import ProgressRing from '../components/shared/ProgressRing.jsx'
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx'
import { formatTempF, formatPrecipIn, formatSnowIn, formatWindMph } from '../utils/units.js'
import { trackEvent } from '../analytics/mixpanel.js'

// ── Copy button ───────────────────────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback: create a textarea
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }
  return (
    <UITooltip content="Copy recommendation for store manager" placement="top">
      <button
        onClick={handleCopy}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
          copied
            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
            : 'bg-white/5 text-slate-400 border-white/10 hover:text-slate-200 hover:bg-white/10'
        }`}
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
        {copied ? 'Copied!' : 'Copy for Manager'}
      </button>
    </UITooltip>
  )
}

// ── Typewriter effect ─────────────────────────────────────────────────────────
function TypewriterText({ text, speed = 20 }) {
  const [displayed, setDisplayed] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!text) return
    setDisplayed('')
    setDone(false)
    let i = 0
    const interval = setInterval(() => {
      i++
      setDisplayed(text.slice(0, i))
      if (i >= text.length) {
        clearInterval(interval)
        setDone(true)
      }
    }, speed)
    return () => clearInterval(interval)
  }, [text, speed])

  return <span>{displayed}{!done && <span className="typewriter-cursor" />}</span>
}

// ── 7-Day Forecast Strip ──────────────────────────────────────────────────────
const SEV_GRADIENTS = {
  0: ['rgba(16,185,129,0.15)', 'rgba(16,185,129,0.3)'],
  1: ['rgba(6,182,212,0.15)', 'rgba(6,182,212,0.3)'],
  2: ['rgba(245,158,11,0.15)', 'rgba(245,158,11,0.3)'],
  3: ['rgba(249,115,22,0.15)', 'rgba(249,115,22,0.3)'],
  4: ['rgba(239,68,68,0.15)', 'rgba(239,68,68,0.3)'],
  5: ['rgba(139,92,246,0.15)', 'rgba(139,92,246,0.3)'],
}

function ForecastStrip({ forecast }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2">
      {forecast.map((day, i) => {
        const [bg, border] = SEV_GRADIENTS[day.severity] ?? SEV_GRADIENTS[0]
        return (
          <motion.div
            key={day.date}
            className="flex-shrink-0 rounded-xl p-3 text-center min-w-[100px]"
            style={{ background: bg, border: `1px solid ${border}` }}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
          >
            <div className="text-xs text-slate-500 mb-1">
              {day.date ? format(parseISO(day.date), 'EEE d') : '--'}
            </div>
            <WeatherBadge category={day.category} size="sm" showIcon={true} />
            <div className="mt-2 space-y-0.5">
              <div className="text-sm font-bold text-slate-100">
                {formatTempF(day.max_temp_c)} / {formatTempF(day.min_temp_c)}
              </div>
              {day.precip_mm > 0 && (
                <div className="text-xs text-blue-400">💧 {formatPrecipIn(day.precip_mm)}</div>
              )}
              {day.snowfall_cm > 0 && (
                <div className="text-xs text-purple-300">❄️ {formatSnowIn(day.snowfall_cm)}</div>
              )}
              <div className="text-xs text-slate-500">💨 {formatWindMph(day.max_wind_kmh)}</div>
            </div>
            <div className="mt-2">
              <SeverityBar severity={day.severity} compact />
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}

// ── Traffic Impact Chart ──────────────────────────────────────────────────────
function TrafficChart({ forecast }) {
  const data = forecast.map((d) => ({
    date: d.date ? format(parseISO(d.date), 'EEE') : '--',
    impact: d.traffic_impact_pct ?? 0,
  }))

  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis
          tick={{ fill: '#64748b', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `${v}%`}
          domain={[-40, 5]}
        />
        <UITooltip
          formatter={(v) => [`${v}%`, 'Traffic Impact']}
          contentStyle={{ background: 'rgba(16,24,48,0.95)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 8 }}
          labelStyle={{ color: '#94a3b8', fontSize: 11 }}
          itemStyle={{ color: '#00d4ff' }}
        />
        <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
        <Bar dataKey="impact" radius={[4, 4, 0, 0]}>
          {data.map((entry, i) => (
            <Cell key={i} fill={entry.impact < 0 ? '#ef4444' : '#10b981'} fillOpacity={0.8} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

// ── Sales History Chart ───────────────────────────────────────────────────────
function SalesChart({ salesHistory }) {
  const data = salesHistory.map((w) => ({
    week: w.sales_date ? format(parseISO(w.sales_date), 'MM/dd') : '--',
    sell_through: Math.round((w.avg_sell_through ?? 0) * 100),
    units: w.total_units ?? 0,
    revenue: w.total_revenue ?? 0,
  }))

  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey="week" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis
          tick={{ fill: '#64748b', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `${v}%`}
          domain={[0, 100]}
        />
        <UITooltip
          formatter={(v) => [`${v}%`, 'Sell-through']}
          contentStyle={{ background: 'rgba(16,24,48,0.95)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 8 }}
          labelStyle={{ color: '#94a3b8', fontSize: 11 }}
          itemStyle={{ color: '#00d4ff' }}
        />
        <Line
          type="monotone"
          dataKey="sell_through"
          stroke="#00d4ff"
          strokeWidth={2.5}
          dot={{ fill: '#00d4ff', r: 3, strokeWidth: 0 }}
          activeDot={{ r: 5, fill: '#00d4ff' }}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

// ── Value at Risk Block ───────────────────────────────────────────────────────

const ACTION_META = {
  LABOR_FLEX:               { icon: Users2,          color: 'text-cyan-400',   bg: 'bg-cyan-500/15',   border: 'border-cyan-500/30',   label: 'Reduce Staffing' },
  LABOR_SURGE_PREP:         { icon: TrendingUp,       color: 'text-sky-400',    bg: 'bg-sky-500/15',    border: 'border-sky-500/30',    label: 'Surge Prep' },
  PREDEPLOYMENT:            { icon: PackageCheck,     color: 'text-purple-400', bg: 'bg-purple-500/15', border: 'border-purple-500/30', label: 'Stock Pre-Positioning' },
  ALLOCATION_HOLD:          { icon: ShieldAlert,      color: 'text-amber-400',  bg: 'bg-amber-500/15',  border: 'border-amber-500/30',  label: 'Hold Allocation' },
  REALLOCATION_OPPORTUNITY: { icon: ArrowRightLeft,   color: 'text-violet-400', bg: 'bg-violet-500/15', border: 'border-violet-500/30', label: 'Redirect Inventory' },
  DC_YARD_CLEARANCE:        { icon: Zap,              color: 'text-red-400',    bg: 'bg-red-500/15',    border: 'border-red-500/30',    label: 'Clear DC Yard' },
  SURGE_WARNING:            { icon: CloudLightning,   color: 'text-blue-400',   bg: 'bg-blue-500/15',   border: 'border-blue-500/30',   label: 'Surge Warning' },
  INBOUND_RISK:             { icon: Factory,          color: 'text-rose-400',   bg: 'bg-rose-500/15',   border: 'border-rose-500/30',   label: 'Inbound Risk' },
  LANE_RISK:                { icon: Route,            color: 'text-indigo-400', bg: 'bg-indigo-500/15', border: 'border-indigo-500/30', label: 'Lane Risk' },
}

const LEAD_TIER_CLS = {
  past:    'text-slate-500 bg-white/5 border-white/10',
  now:     'text-red-400 bg-red-500/15 border-red-500/30',
  soon:    'text-amber-400 bg-amber-500/15 border-amber-500/30',
  planned: 'text-cyan-400 bg-cyan-500/15 border-cyan-500/30',
  far:     'text-slate-400 bg-white/5 border-white/10',
  unknown: 'text-slate-500 bg-white/5 border-white/10',
}

const ACTION_TIPS = {
  'REDUCE STAFFING':              'Flex labor down during the weather window — forecast projects a significant drop in foot traffic. Reducing variable shifts avoids paying for idle hours while protecting gross margin.',
  'INCREASE STAFFING':            'Add floor staff in the 48-hour pre-storm window. Customers typically surge to stock up before a storm hits — extra coverage captures this demand spike.',
  'STOCK PRE POSITIONING':        'Advance inbound replenishment before the weather window closes. Stores in distant lanes risk stock-outs if shipments are delayed 3–5 days during the storm.',
  'HOLD ALLOCATION':              'Pause DC-to-store replenishment. High sell-through sensitivity means inventory sent now may become trapped if traffic drops and sell-through collapses.',
  'REDIRECT INVENTORY':           'Divert pending allocations from this weather-impacted store to high sell-through sunny stores. Prevents capital from being trapped in a low-traffic location.',
  'CLEAR DC YARD':                'Stage and dispatch priority outbound trailers before DC-area weather onset. Once severity-4 conditions hit, yard operations and driver availability will be severely limited.',
  'SURGE PREP — STOCK UP':        'Calm today and tomorrow, but a severe storm arrives on day 3–4. Pre-storm customers surge to stock up — boost floor stock now to capture this 48-hour demand spike.',
  'ALERT SUPPLIERS — DELAY INBOUND': 'DC-area severe weather puts inbound shipments at risk of detention and yard congestion. Alert upstream suppliers to delay inbound until the weather window clears.',
  'REROUTE OR EXPEDITE LANES':    'Primary outbound lanes from this DC are HIGH RISK. Evaluate alternate carrier routing or pre-position trailers to prevent delivery disruptions to downstream stores.',
}

const URGENCY_REASON_COPY = {
  event_imminent:   'Storm hits in the next 48 hours.',
  hourly_onset:     'Severity-4 onset is within 24 hours.',
  low_wos:          'Weeks-on-hand below 1.5 — near-stockout dominates timing.',
  promo_escalation: 'Active promotion lifts demand during the storm window.',
  trigger_imminent: 'Moderate weather (sev≥3) starts in the next 48h even though peak (sev≥4) is later.',
  planned:          'You have planning runway — act inside the 48–96h window.',
}

function fmtPnl(v) {
  if (!v) return '—'
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(1)}K`
  return `$${v}`
}

// ── Briefing hover tooltip portal ─────────────────────────────────────────────

function BriefingTooltip({ hoverTarget, briefings }) {
  if (!hoverTarget) return null
  const { id, rect } = hoverTarget
  const briefing = briefings[id]
  const spaceBelow = window.innerHeight - rect.bottom
  const above = spaceBelow < 200

  const style = {
    position: 'fixed',
    zIndex: 9999,
    width: 380,
    left: Math.min(rect.left + 40, window.innerWidth - 396),
    pointerEvents: 'none',
    background: 'rgba(6, 12, 36, 0.97)',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 0 24px rgba(0,212,255,0.1), 0 12px 48px rgba(0,0,0,0.75)',
    border: '1px solid rgba(0,212,255,0.22)',
    borderRadius: '0.75rem',
    padding: '0.875rem',
    ...(above ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 }),
  }

  return createPortal(
    <motion.div
      style={style}
      initial={{ opacity: 0, y: above ? 6 : -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Bot size={11} className="text-cyan-400 flex-shrink-0" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">AI Manager Briefing</span>
      </div>
      {!briefing || briefing.loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <RefreshCw size={10} className="animate-spin text-cyan-400" />
          Generating briefing…
        </div>
      ) : briefing.error ? (
        <p className="text-xs text-slate-500 italic">Briefing unavailable.</p>
      ) : (
        <p className="text-xs text-slate-300 leading-relaxed line-clamp-5">{briefing.text}</p>
      )}
    </motion.div>,
    document.body,
  )
}

// ── Action explain dialog ─────────────────────────────────────────────────────

function TriggerStat({ label, value, sub, colorCls }) {
  return (
    <div className={`rounded-xl p-3 border text-center ${colorCls}`}>
      <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">{label}</div>
      <div className="text-xl font-black text-slate-100">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  )
}

function ActionExplainDialog({ action, onClose }) {
  const meta = ACTION_META[action.category] ?? { icon: Activity, color: 'text-slate-400', bg: 'bg-white/5', border: 'border-white/10', label: action.category }
  const Icon = meta.icon
  const { data, isLoading, isError } = useActionExplain(action.id)

  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handle)
    return () => document.removeEventListener('keydown', handle)
  }, [onClose])

  const wos = action.wos
  const sens = action.sensitivity_score
  const severity = action.trigger_severity ?? 0

  const wosCls = wos == null ? 'bg-white/5 border-white/10' : wos < 4 ? 'bg-red-500/10 border-red-500/20' : wos < 8 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-emerald-500/10 border-emerald-500/20'
  const wosValCls = wos == null ? 'text-slate-400' : wos < 4 ? 'text-red-400' : wos < 8 ? 'text-amber-400' : 'text-emerald-400'
  const sensCls = sens != null && sens > 0.15 ? 'bg-orange-500/10 border-orange-500/20' : 'bg-white/5 border-white/10'
  const sensValCls = sens != null && sens > 0.15 ? 'text-orange-400' : 'text-slate-300'

  // Parse narrative into paragraphs
  const paragraphs = (data?.narrative ?? '')
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <motion.div
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto scrollbar-thin"
        style={{
          background: 'rgba(6,12,36,0.97)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0,212,255,0.22)',
          borderRadius: '0.75rem',
          boxShadow: '0 0 40px rgba(0,212,255,0.08), 0 24px 64px rgba(0,0,0,0.85)',
        }}
        initial={{ scale: 0.95, y: 24 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* ── Header ── */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${meta.bg} ${meta.border}`}>
                <Icon size={18} className={meta.color} />
              </div>
              <div>
                <div className={`text-sm font-black uppercase tracking-wide ${meta.color}`}>
                  {action.action_text}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{meta.label}</div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-300 transition-colors p-1 rounded-lg hover:bg-white/5"
            >
              <X size={17} />
            </button>
          </div>

          {/* ── Trigger stats ── */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className={`rounded-xl p-3 border text-center ${severity >= 4 ? 'bg-red-500/10 border-red-500/20' : severity >= 3 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-white/5 border-white/10'}`}>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Severity</div>
              <div className={`text-xl font-black ${severity >= 4 ? 'text-red-400' : severity >= 3 ? 'text-amber-400' : 'text-slate-300'}`}>{severity}/5</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{action.trigger_date ?? '—'}</div>
            </div>
            <div className={`rounded-xl p-3 border text-center ${wosCls}`}>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Weeks on Hand</div>
              <div className={`text-xl font-black ${wosValCls}`}>{wos != null ? `${wos.toFixed(1)}w` : '—'}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">inventory</div>
            </div>
            <div className={`rounded-xl p-3 border text-center ${sensCls}`}>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Sensitivity</div>
              <div className={`text-xl font-black ${sensValCls}`}>{sens != null ? `${(sens * 100).toFixed(1)}%` : '—'}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">demand spread</div>
            </div>
          </div>

          {/* ── System trigger (justification) ── */}
          <div className="mb-5 p-3 rounded-xl bg-white/5 border border-white/10">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">System Trigger</div>
            <p className="text-sm text-slate-300 leading-relaxed">{action.justification}</p>
          </div>

          {/* ── P&L formula ── */}
          {action.pnl_formula && (
            <div className="mb-5 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 mb-1.5">P&L Formula</div>
              <div className="text-xs text-slate-200 font-mono leading-relaxed">{action.pnl_formula}</div>
              <div className="mt-2 flex items-center gap-2">
                <DollarSign size={11} className="text-emerald-400" />
                <span className="text-sm font-black text-emerald-400">{fmtPnl(action.pnl_estimate ?? 0)}</span>
                <span className="text-xs text-slate-500">estimated P&L impact</span>
              </div>
            </div>
          )}

          {/* ── AI narrative ── */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Bot size={12} className="text-cyan-400 flex-shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">AI Analysis</span>
            </div>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 0.9, 0.75, 1, 0.85, 0.6].map((w, i) => (
                  <div key={i} className="h-3 bg-white/5 rounded animate-pulse" style={{ width: `${w * 100}%` }} />
                ))}
              </div>
            ) : isError || !data ? (
              <p className="text-slate-500 text-sm italic">
                AI explanation unavailable — review the trigger factors and formula above.
              </p>
            ) : (
              <div className="space-y-3">
                {paragraphs.map((para, i) => (
                  <p key={i} className="text-sm text-slate-300 leading-relaxed">{para}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  )
}

// ── Single action row ─────────────────────────────────────────────────────────

function ActionRow({ action, now, isDc, onClick, onMouseEnter, onMouseLeave }) {
  const meta = ACTION_META[action.category] ?? { icon: Activity, color: 'text-slate-400', bg: 'bg-white/5', border: 'border-white/10', label: action.category }
  const Icon = meta.icon
  const isImmediate = action.urgency?.startsWith('Immediate')
  const hours = leadTimeHours(action.event_at, now)
  const tier = leadTimeTier(hours)
  const leadCls = LEAD_TIER_CLS[tier] ?? LEAD_TIER_CLS.unknown

  // For DC-level actions use the proportional store_pnl; for store actions use pnl_estimate.
  const displayPnl = isDc ? (action.store_pnl ?? 0) : (action.pnl_estimate ?? 0)
  const pnlTip = isDc
    ? `Your store's share of the DC-wide impact: ${fmtPnl(displayPnl)} out of ${fmtPnl(action.pnl_estimate)} total across ${action.dc_store_count ?? '?'} stores served by ${action.dc}.${action.pnl_formula ? `\n\nFormula: ${action.pnl_formula}` : ''}`
    : action.pnl_formula ?? `Estimated P&L impact: ${fmtPnl(displayPnl)}`

  const urgencyTip = (() => {
    const reason = action.urgency_reason
    const base = URGENCY_REASON_COPY[reason] ?? ''
    let extra = ''
    if (reason === 'low_wos' && action.wos != null) extra = ` (WOS = ${action.wos.toFixed(1)} wks).`
    if (typeof hours === 'number' && hours >= 24 && isImmediate)
      extra += ` Event itself is ${Math.round(hours)}h out — urgency reflects when you must act.`
    return `${action.urgency}${base ? ` — ${base}` : ''}${extra}`.trim()
  })()

  const leadTip = action.event_at
    ? `Event at ${new Date(action.event_at).toLocaleString()} — the storm or disruption window. Refreshed every 10 min.`
    : 'No scheduled event time available.'

  return (
    <motion.div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer select-none ${meta.bg} ${meta.border}`}
      whileHover={{ scale: 1.012, y: -1, boxShadow: '0 4px 24px rgba(0,0,0,0.35)' }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Category icon */}
      <UITooltip content={ACTION_TIPS[action.action_text] ?? meta.label} placement="right">
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-black/20 border cursor-help ${meta.border}`}>
          <Icon size={14} className={meta.color} />
        </div>
      </UITooltip>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <UITooltip content={ACTION_TIPS[action.action_text] ?? null} placement="top">
          <div className={`text-xs font-black uppercase tracking-wide cursor-help ${meta.color}`}>
            {action.action_text}
          </div>
        </UITooltip>
        <div className="text-[11px] text-slate-400 mt-0.5 truncate" title={action.justification}>
          {action.justification?.slice(0, 95)}{(action.justification?.length ?? 0) > 95 ? '…' : ''}
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <UITooltip content={leadTip} placement="top">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tabular-nums border cursor-help ${leadCls}`}>
            <Clock size={9} />
            {formatLeadTime(hours)}
          </span>
        </UITooltip>

        <UITooltip content={urgencyTip} placement="top">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border cursor-help ${
            isImmediate ? 'text-red-400 bg-red-500/15 border-red-500/30' : 'text-amber-400 bg-amber-500/15 border-amber-500/30'
          }`}>
            {isImmediate && <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />}
            {isImmediate ? '24h' : '48–96h'}
          </span>
        </UITooltip>

        <UITooltip content={pnlTip} placement="left">
          <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-400 tabular-nums w-16 text-right cursor-help">
            {fmtPnl(displayPnl)}
            {isDc && <Info size={9} className="text-slate-500 flex-shrink-0" />}
          </span>
        </UITooltip>
      </div>
    </motion.div>
  )
}

// ── Value at Risk container ───────────────────────────────────────────────────

function ValueAtRiskBlock({ storeId, dc }) {
  const { data, isLoading } = useStoreActions(storeId)
  const now = useNow(10 * 60 * 1000)

  const [briefings, setBriefings] = useState({})
  const [hoverTarget, setHoverTarget] = useState(null)
  const hoverTimerRef = useRef(null)
  const [explainAction, setExplainAction] = useState(null)

  const fetchBriefing = useCallback(async (actionId) => {
    if (briefings[actionId]) return
    setBriefings((prev) => ({ ...prev, [actionId]: { loading: true } }))
    try {
      const { data: d } = await client.get(`/actions/${actionId}/briefing`)
      setBriefings((prev) => ({ ...prev, [actionId]: { text: d.briefing, loading: false } }))
    } catch {
      setBriefings((prev) => ({ ...prev, [actionId]: { error: true, loading: false } }))
    }
  }, [briefings])

  const handleRowEnter = useCallback((action, e) => {
    fetchBriefing(action.id)
    const rect = e.currentTarget.getBoundingClientRect()
    clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = setTimeout(() => setHoverTarget({ id: action.id, rect }), 350)
  }, [fetchBriefing])

  const handleRowLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current)
    setHoverTarget(null)
  }, [])

  const totalVar = data?.total_value_at_risk ?? 0
  const storeActions = data?.store_actions ?? []
  const dcActions = data?.dc_actions ?? []

  if (isLoading) {
    return (
      <div className="glass-card p-5 animate-pulse">
        <div className="h-4 w-48 bg-white/10 rounded mb-4" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-white/5 rounded-xl" />)}
        </div>
      </div>
    )
  }

  if (storeActions.length === 0 && dcActions.length === 0) {
    return (
      <div className="glass-card p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <DollarSign size={16} className="text-emerald-400" />
          <h2 className="font-bold text-slate-200 text-sm">Value at Risk</h2>
        </div>
        <p className="text-slate-500 text-sm">No active actions for this store — weather conditions are clear.</p>
      </div>
    )
  }

  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <DollarSign size={16} className="text-emerald-400" />
          <h2 className="font-bold text-slate-200 text-sm">Value at Risk</h2>
          <span className="text-xs text-slate-500">Prescriptive actions for this store</span>
        </div>
        {totalVar > 0 && (
          <UITooltip
            content="Sum of store-level P&L impact plus this store's proportional share of DC-level signals."
            placement="left"
          >
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 cursor-help">
              <span className="text-xs text-slate-400">Total at risk:</span>
              <span className="text-sm font-black text-emerald-400">{fmtPnl(totalVar)}</span>
            </div>
          </UITooltip>
        )}
      </div>

      {storeActions.length > 0 && (
        <div className="space-y-2 mb-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Store-Level Actions</div>
          {storeActions.map((action) => (
            <ActionRow
              key={action.id}
              action={action}
              now={now}
              isDc={false}
              onClick={() => setExplainAction(action)}
              onMouseEnter={(e) => handleRowEnter(action, e)}
              onMouseLeave={handleRowLeave}
            />
          ))}
        </div>
      )}

      {dcActions.length > 0 && (
        <div className="space-y-2">
          <UITooltip
            content={`DC-level signals that affect all ${data?.dc_store_count ?? 'N'} stores served by ${dc}. The P&L shown is this store's proportional share.`}
            placement="right"
          >
            <div className="flex items-center gap-1.5 mb-2 cursor-help">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                DC-Level Signals — {dc}
              </div>
              <Info size={9} className="text-slate-600" />
            </div>
          </UITooltip>
          {dcActions.map((action) => (
            <ActionRow
              key={action.id}
              action={action}
              now={now}
              isDc={true}
              onClick={() => setExplainAction(action)}
              onMouseEnter={(e) => handleRowEnter(action, e)}
              onMouseLeave={handleRowLeave}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {hoverTarget && (
          <BriefingTooltip key={hoverTarget.id} hoverTarget={hoverTarget} briefings={briefings} />
        )}
      </AnimatePresence>

      {explainAction && (
        <ActionExplainDialog
          key={explainAction.id}
          action={explainAction}
          onClose={() => setExplainAction(null)}
        />
      )}
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
// ── Traffic-Prediction card ────────────────────────────────────────────────
// Calls /api/stores/:id/traffic-prediction. The response carries one row per
// forecast day with `source: 'model' | 'heuristic'` so the card can show the
// user where the number came from. Sensitivity score is intentionally NOT in
// this card — it lives in the existing header KPI strip and the Sensitivity
// row above. We just keep this card focused on the per-day prediction.
function TrafficPredictionCard({ storeId }) {
  const { data, isLoading, isError } = useStoreTrafficPrediction(storeId, { days: 7 })

  const formatPct = (v) =>
    v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`
  const formatRaw = (v) =>
    v == null ? '—' : Math.round(v).toLocaleString()

  return (
    <motion.div
      className="glass-card p-5 mb-6"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 }}
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-slate-200 flex items-center gap-2">
          <TrendingUp size={16} className="text-cyan-400" />
          Predicted Traffic — Next 7 Days
        </h2>
        {data && (
          <span
            className={`text-[10px] px-2 py-0.5 rounded-md uppercase tracking-widest border ${
              data.model_status === 'loaded'
                ? 'text-cyan-300 bg-cyan-500/10 border-cyan-500/30'
                : 'text-amber-300 bg-amber-500/10 border-amber-500/30'
            }`}
            title={`Model status: ${data.model_status}`}
          >
            {data.model_status === 'loaded' ? `model · ${data.model_version}` : 'fallback'}
          </span>
        )}
      </div>

      {isLoading && (
        <div className="text-xs text-slate-500 py-6 text-center">Predicting…</div>
      )}
      {isError && (
        <div className="text-xs text-amber-400/80 py-2">
          Couldn't fetch traffic predictions. The action engine will still use
          the historical severity heuristic.
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {data.predictions.map((p) => {
              const pct = p.predicted_pct_delta
              const tone =
                pct == null
                  ? 'text-slate-400'
                  : pct < -10 ? 'text-red-400'
                  : pct < -3  ? 'text-amber-400'
                  : pct >  3  ? 'text-emerald-400'
                  : 'text-slate-300'
              return (
                <div
                  key={p.date}
                  className="rounded-lg border border-white/5 bg-white/[0.02] p-2 text-center"
                  title={`${p.weather_category ?? ''} · severity ${p.weather_severity}`}
                >
                  <div className="text-[10px] text-slate-500 mb-1">{p.date.slice(5)}</div>
                  <div className={`text-sm font-bold tabular-nums ${tone}`}>
                    {formatPct(pct)}
                  </div>
                  {p.predicted_raw_traffic != null && (
                    <div className="text-[10px] text-slate-500 mt-0.5 tabular-nums">
                      {formatRaw(p.predicted_raw_traffic)}
                    </div>
                  )}
                  <div
                    className={`mt-1 text-[9px] uppercase tracking-widest ${
                      p.source === 'model' ? 'text-cyan-500/70' : 'text-amber-500/70'
                    }`}
                  >
                    {p.source}
                  </div>
                </div>
              )
            })}
          </div>
          {data.model_status !== 'loaded' && (
            <p className="text-[11px] text-slate-500 mt-3">
              Predictions shown are the historical severity heuristic — the
              CatBoost model is currently {data.model_status}. See <code>/api/health</code>.
            </p>
          )}
        </>
      )}
    </motion.div>
  )
}

export default function StoreDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useStore(id)

  // Dedupe Briefing Viewed across re-renders for the same (store, briefing) pair.
  const lastBriefingTrackedRef = useRef(null)
  useEffect(() => {
    const briefing = data?.labor_recommendation
    if (!briefing) return
    const key = `${id}`
    if (lastBriefingTrackedRef.current === key) return
    lastBriefingTrackedRef.current = key
    trackEvent('Briefing Viewed', {
      surface: 'store_detail',
      node_type: 'Store',
      briefing_length: briefing.length,
    })
  }, [id, data?.labor_recommendation])

  // ≥50% visible for ≥1s → fire once per section per page visit.
  const sectionsFiredRef = useRef(new Set())
  const sectionTimersRef = useRef(new Map())
  useEffect(() => {
    if (!data) return
    sectionsFiredRef.current = new Set()
    const timers = sectionTimersRef.current
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const name = entry.target.getAttribute('data-section')
        if (!name) continue
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (sectionsFiredRef.current.has(name)) continue
          if (timers.has(name)) continue
          const t = setTimeout(() => {
            timers.delete(name)
            if (sectionsFiredRef.current.has(name)) return
            sectionsFiredRef.current.add(name)
            trackEvent('Store Detail Section Viewed', { section: name, store_id: id })
          }, 1000)
          timers.set(name, t)
        } else {
          const t = timers.get(name)
          if (t) { clearTimeout(t); timers.delete(name) }
        }
      }
    }, { threshold: [0.5] })
    document.querySelectorAll('[data-section]').forEach((el) => observer.observe(el))
    return () => {
      observer.disconnect()
      timers.forEach((t) => clearTimeout(t))
      timers.clear()
    }
  }, [data, id])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text={`Loading store ${id}...`} size="lg" />
      </div>
    )
  }

  if (isError || (!isLoading && !data)) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="glass-card p-8">
          <AlertTriangle size={48} className="text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-200 mb-2">Store not found</h2>
          <p className="text-slate-400 mb-6 text-sm">Store {id} may not exist or is unavailable.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate(-1)} className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 transition-colors text-sm">
              Go Back
            </button>
            <button onClick={() => refetch()} className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors text-sm">
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  const store = data
  const signalSev = store.max_severity_7d ?? 0
  const sens = store.sensitivity_score ?? 0
  const signal = signalSev >= 4 && sens > 0.15 ? 'STOP' : 'GO'

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 xl:py-6 space-y-6 min-h-[calc(100vh-4rem)]">
      {/* ── Breadcrumb / Back ── */}
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-slate-400 hover:text-cyan-400 transition-colors"
        >
          <ArrowLeft size={14} />
          Back
        </button>
        <span className="text-slate-700">/</span>
        <span className="text-slate-500">Stores</span>
        <span className="text-slate-700">/</span>
        <span className="text-slate-300 font-mono">{store.store_id}</span>
      </div>

      {/* ── Store Header ── */}
      <motion.div
        data-section="kpis"
        className="glass-card p-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-black text-cyan-400 font-mono">
                #{store.store_id}
              </h1>
              <SignalBadge signal={signal} size="md" />
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <MapPin size={14} />
              <span className="font-medium text-slate-200">
                {store.city}, {store.state}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-slate-500 text-sm">
              <Warehouse size={13} />
              <span>Primary DC: </span>
              <UITooltip content={DC_INFO[store.primary_dc] ?? store.dc_name} placement="bottom">
                <span className="font-bold text-cyan-400 cursor-help border-b border-dashed border-cyan-700">{store.primary_dc}</span>
              </UITooltip>
              <span className="text-slate-600">—</span>
              <span>{store.dc_name}</span>
            </div>
          </div>
          <div className="flex gap-4">
            <UITooltip
              content={
                store.current_wos < 4
                  ? 'Critical — less than 4 weeks of inventory on hand'
                  : store.current_wos < 8
                  ? 'Low — monitor closely, replenishment may be needed soon'
                  : 'Healthy stock level — sufficient inventory on hand'
              }
              placement="top"
            >
              <div className="text-center cursor-help">
                <div className="text-xs text-slate-500 mb-1 border-b border-dashed border-slate-600">WOS</div>
                <div className={`text-2xl font-black ${
                  store.current_wos < 4 ? 'text-red-400' :
                  store.current_wos < 8 ? 'text-amber-400' :
                  'text-emerald-400'
                }`}>
                  {(store.current_wos ?? 0).toFixed(1)}
                </div>
              </div>
            </UITooltip>
            <UITooltip
              content="Sell-through rate spread (MAX–MIN) over 12 weeks. Values above 15% indicate high demand volatility and trigger STOP signals."
              placement="top"
            >
              <div className="text-center cursor-help">
                <div className="text-xs text-slate-500 mb-1 border-b border-dashed border-slate-600">Sensitivity</div>
                <div className={`text-2xl font-black ${sens > 0.15 ? 'text-amber-400' : 'text-slate-300'}`}>
                  {(sens * 100).toFixed(1)}%
                </div>
              </div>
            </UITooltip>
            <UITooltip
              content={`Highest weather severity forecast in the next 7 days — ${['Clear', 'Minor', 'Moderate', 'Significant', 'Severe', 'Extreme'][Math.min(5, signalSev)] ?? ''} (${signalSev}/5)`}
              placement="top"
            >
              <div className="text-center cursor-help">
                <div className="text-xs text-slate-500 mb-1 border-b border-dashed border-slate-600">Max Severity</div>
                <SeverityBar severity={signalSev} compact />
              </div>
            </UITooltip>
          </div>
        </div>
      </motion.div>

      {/* ── 7-Day Forecast ── */}
      <motion.div
        data-section="weather"
        className="glass-card p-5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h2 className="font-bold text-slate-200 mb-4 flex items-center gap-2">
          <span className="text-lg">🌤️</span>
          7-Day Forecast
          <span className="text-xs text-slate-500 font-normal ml-auto">
            {store.city !== 'Unknown' ? `${store.city}, ${store.state}` : store.dc_name}
          </span>
        </h2>
        {store.forecast?.length > 0 ? (
          <ForecastStrip forecast={store.forecast} />
        ) : (
          <p className="text-slate-500 text-sm">No forecast data available</p>
        )}
      </motion.div>

      {/* ── Predicted Traffic (model or fallback) ── */}
      <TrafficPredictionCard storeId={store.store_id} />

      {/* ── Value at Risk ── */}
      <motion.div
        data-section="actions_for_store"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <ValueAtRiskBlock storeId={store.store_id} dc={store.primary_dc} />
      </motion.div>

      {/* ── 2-col: Traffic Impact + Lane Reliability ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Traffic Impact */}
        <motion.div
          data-section="traffic"
          className="glass-card p-5"
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
        >
          <h2 className="font-bold text-slate-200 mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-amber-400" />
            Projected Traffic Impact
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            Estimated foot traffic change based on weather severity
          </p>
          {store.forecast?.length > 0 ? (
            <TrafficChart forecast={store.forecast} />
          ) : (
            <p className="text-slate-500 text-sm">No forecast data available</p>
          )}
        </motion.div>

        {/* Lane Reliability */}
        <motion.div
          data-section="lane_reliability"
          className="glass-card p-5"
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-bold text-slate-200 mb-4 flex items-center gap-2">
            <Truck size={16} className="text-cyan-400" />
            Lane Reliability
          </h2>
          <div className="flex items-center gap-6 mb-4">
            <UITooltip content="Percentage of shipments from this DC that arrived on or before the estimated delivery date" placement="top">
              <div className="cursor-help">
                <ProgressRing
                  value={store.lane_reliability?.pct_on_time ?? 100}
                  size={90}
                  strokeWidth={7}
                  color="#10b981"
                  label="On Time"
                />
              </div>
            </UITooltip>
            <div className="space-y-3 flex-1">
              <div>
                <UITooltip content="Average hours between estimated and actual delivery date across all shipments from this DC to this store's lane" placement="top">
                  <div className="text-xs text-slate-500 mb-0.5 cursor-help border-b border-dashed border-slate-600 inline-block">Avg Delivery Delay</div>
                </UITooltip>
                <div className="flex items-center gap-2">
                  <Clock size={13} className="text-amber-400" />
                  <span className={`text-lg font-bold ${
                    (store.lane_reliability?.avg_delay_hours ?? 0) > 24 ? 'text-red-400' :
                    (store.lane_reliability?.avg_delay_hours ?? 0) > 4 ? 'text-amber-400' :
                    'text-emerald-400'
                  }`}>
                    {(store.lane_reliability?.avg_delay_hours ?? 0).toFixed(1)}h
                  </span>
                </div>
              </div>
              <div>
                <UITooltip content="Number of carton-level shipment records used to compute lane reliability statistics" placement="top">
                  <div className="text-xs text-slate-500 mb-0.5 cursor-help border-b border-dashed border-slate-600 inline-block">Shipments Analysed</div>
                </UITooltip>
                <div className="text-slate-300 font-medium">
                  {(store.lane_reliability?.total_shipments ?? 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-0.5">DC</div>
                <UITooltip content={DC_INFO[store.lane_reliability?.dc ?? store.primary_dc] ?? ''} placement="right">
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 cursor-help">
                    {store.lane_reliability?.dc ?? store.primary_dc}
                  </span>
                </UITooltip>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* ── AI Labor Recommendation ── */}
      <motion.div
        data-section="briefing"
        className="glass-card p-5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <Bot size={15} className="text-purple-400" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-slate-200 text-sm">Labor Recommendation</h2>
            <p className="text-xs text-slate-500">AI-generated based on forecast & store profile</p>
          </div>
          <CopyButton text={store.labor_recommendation ?? ''} />
        </div>
        <div className="rounded-lg bg-black/30 p-4 text-sm text-slate-300 leading-relaxed border border-white/5 font-mono">
          <TypewriterText text={store.labor_recommendation ?? ''} speed={15} />
        </div>
      </motion.div>

      {/* ── Recent Sales History ── */}
      <motion.div
        data-section="sales_history"
        className="glass-card p-5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="font-bold text-slate-200 mb-1 flex items-center gap-2">
          <TrendingUp size={16} className="text-purple-400" />
          12-Week Sales History
        </h2>
        <p className="text-xs text-slate-500 mb-4">Weekly sell-through rate</p>

        {store.sales_history?.length > 0 ? (
          <>
            <SalesChart salesHistory={store.sales_history} />

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/5">
              <div className="text-center">
                <div className="text-xs text-slate-500">Avg Sell-Through</div>
                <div className="text-lg font-bold text-cyan-400">
                  {(
                    (store.sales_history.reduce((s, w) => s + (w.avg_sell_through ?? 0), 0) /
                      store.sales_history.length) *
                    100
                  ).toFixed(1)}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-slate-500">Total Units Sold</div>
                <div className="text-lg font-bold text-purple-400">
                  {store.sales_history
                    .reduce((s, w) => s + (w.total_units ?? 0), 0)
                    .toLocaleString()}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-slate-500">Total Revenue</div>
                <div className="text-lg font-bold text-emerald-400">
                  ${(
                    store.sales_history.reduce((s, w) => s + (w.total_revenue ?? 0), 0) / 1000
                  ).toFixed(1)}K
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-slate-500 text-sm">No sales history available for this store.</p>
        )}
      </motion.div>
    </div>
  )
}
